// Modal — centered dialog. Uses MUI Dialog for behavior (focus trap, portal,
// Esc/backdrop close, a11y) and paints the surface with our tokens. Visual
// chrome (header/body/footer) is our own classes. Lazy-friendly: render it
// only when needed and MUI handles mount/unmount transitions.
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import { cx } from '../../../utils/cx';
import { CloseIcon } from '../../icons/Icons';
import './modal.css';

const MAX_WIDTH = { sm: 420, md: 560, lg: 760, xl: 960 };

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  actions,
  children,
  dividers = false,
  flush = false,
  ariaLabelledBy,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={ariaLabelledBy || (title ? 'modal-title' : undefined)}
      slotProps={{
        paper: {
          className: cx('modal__paper', flush && 'modal__paper--flush'),
          sx: { maxWidth: MAX_WIDTH[size], width: '100%', m: 2, borderRadius: 'var(--r-lg)' },
        },
        backdrop: { sx: { backgroundColor: 'rgba(10,12,20,0.45)' } },
      }}
    >
      {title && (
        <header className="modal__head">
          <div className="modal__heading">
            <h2 className="modal__title" id="modal-title">
              {title}
            </h2>
            {subtitle && <p className="modal__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </header>
      )}
      <div
        className={cx(
          'modal__body',
          dividers && 'modal__body--dividers',
          flush && 'modal__body--flush',
        )}
      >
        {children}
      </div>
      {actions && <footer className="modal__footer">{actions}</footer>}
    </Dialog>
  );
}

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  /** Secondary line under the title (scope/period/total). Omitted → header is unchanged. */
  subtitle: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  /** Footer slot (buttons). Submit-right / cancel-left convention. */
  actions: PropTypes.node,
  dividers: PropTypes.bool,
  /** Full-bleed mode: drops body padding and makes the body the scroll region
   *  (paper becomes a bounded flex column) so the caller can pin its own
   *  header/footer. Default false keeps the padded, whole-paper-scroll behavior. */
  flush: PropTypes.bool,
  /** Id of the caller's own heading when `title` is not used (flush dialogs pin their own header). */
  ariaLabelledBy: PropTypes.string,
  children: PropTypes.node,
};

export default Modal;
